import { useEffect, useState } from 'react'
import { useToast } from "@/components/ui/use-toast"
import { supabase } from '@/lib/supabase'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { RefreshCw } from 'lucide-react'

interface Route {
    method: string
    path: string
    pattern: string
    destination: string
}

interface RoutesResponse {
    message: string
    count: number
    routes: Route[]
}

export default function TestFunctions() {
    const [routes, setRoutes] = useState<Route[]>([])
    const [loading, setLoading] = useState(false)
    const { toast } = useToast()

    const fetchRoutes = async () => {
        setLoading(true)
        try {
            const session = await supabase.auth.getSession()
            if (!session.data.session) {
                throw new Error('No session found')
            }

            const workerUrl = import.meta.env.VITE_WORKERS_URL
            const response = await fetch(`${workerUrl}/routes`, {
                headers: {
                    'Authorization': `Bearer ${session.data.session.access_token}`
                }
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data: RoutesResponse = await response.json()
            setRoutes(data.routes)
        } catch (error) {
            console.error('Error fetching routes:', error)
            toast({
                title: "Error fetching routes",
                description: "There was a problem fetching the routes. Please try again.",
                variant: "destructive",
            })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchRoutes()
    }, [])

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">API Routes</h2>
                <Button 
                    onClick={fetchRoutes} 
                    disabled={loading}
                    variant="outline"
                    size="sm"
                >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>
            
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[100px]">Method</TableHead>
                            <TableHead>Path</TableHead>
                            <TableHead>Pattern</TableHead>
                            <TableHead className="w-[200px]">Handler</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {routes.map((route, index) => (
                            <TableRow key={`${route.method}-${route.path}-${index}`}>
                                <TableCell className="font-mono">
                                    <span className={`
                                        px-2 py-1 rounded text-xs
                                        ${route.method === 'GET' ? 'bg-blue-100 text-blue-800' : ''}
                                        ${route.method === 'POST' ? 'bg-green-100 text-green-800' : ''}
                                        ${route.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' : ''}
                                        ${route.method === 'DELETE' ? 'bg-red-100 text-red-800' : ''}
                                        ${route.method === 'ALL' ? 'bg-gray-100 text-gray-800' : ''}
                                    `}>
                                        {route.method}
                                    </span>
                                </TableCell>
                                <TableCell className="font-mono text-sm">{route.path}</TableCell>
                                <TableCell className="font-mono text-sm text-muted-foreground">{route.pattern}</TableCell>
                                <TableCell className="text-sm">
                                    <span className={`
                                        px-2 py-1 rounded
                                        ${route.destination.includes('Worker') ? 'bg-purple-100 text-purple-800' : ''}
                                        ${route.destination.includes('Middleware') ? 'bg-orange-100 text-orange-800' : ''}
                                        ${route.destination === 'UnknownHandler' ? 'bg-red-100 text-red-800' : ''}
                                        ${!route.destination.includes('Worker') && !route.destination.includes('Middleware') && route.destination !== 'UnknownHandler' ? 'bg-blue-100 text-blue-800' : ''}
                                    `}>
                                        {route.destination}
                                    </span>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
} 